<template>
  <LcTable
    v-for="(group, index) in groups"
    :key="index"
    bordered
    class="results-tier-table-combined"
  >
    <thead v-if="group !== 'DEFAULT'" class="table-head">
      <tr>
        <th>
          {{ $t(`tiertable.combined.tier.heading.${group}`) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(tierTable, tierTableIndex) in combinedTierTablesByGroup[group]"
        :key="tierTable.name"
      >
        <td v-if="tierTable.group === group">
          <LcTable padding="small">
            <thead v-if="tierTable.name">
              <tr>
                <th colspan="2" scope="col">
                  {{
                    $t(
                      `tiertable.combined.tier.${tierTable.name}`,
                      $te(`tiertable.combined.tier.group.${group}`)
                        ? {
                            group: `tiertable.combined.tier.group.${group}`,
                          }
                        : {},
                    )
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(prize, prizeIndex) in tierTable.prizes"
                :key="`prize-${tierTableIndex}-${prizeIndex}`"
              >
                <td>
                  <TierTableLineItem
                    v-bind="{ addOns, licensedTerritory, lotteryId }"
                    :fields="prize.fields"
                  />
                </td>

                <td>
                  <template v-if="type === 'winners'">
                    <div>{{ prize.winners }}</div>
                  </template>
                  <template v-else>
                    <template v-if="prize.type === PrizeTierType.FreeTicket">
                      <div class="text-accent-accent-success">
                        {{ $t('tiertable.freeticket') }}
                      </div>
                    </template>
                    <template v-else>
                      <div>{{ new Money(prize.prize) }}</div>
                    </template>
                  </template>
                </td>
              </tr>
            </tbody>
          </LcTable>
        </td>
      </tr>
      <tr />
    </tbody>
  </LcTable>
</template>

<script lang="ts" setup>
import type { LcLotteryNumber } from '@lottocom/frontend-components'

import Money from '~~/models/Money'

import {
  type AddOn,
  type Money as MoneyType,
  type NumberLottery,
  type PrizePayload,
  type PrizeTier,
  type PrizeTierRequirements,
  PrizeTierType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type {
  LineItemField,
  LineItemProps,
} from '../../../../../../app/components/LineItem/LineItem.vue'

export type TierTableType = 'prizes' | 'winners'

type CombinedTierTable = {
  group?: string
  name?: string
  prizes: TierDisplay[]
}

type LotteryTierTableMultipleProps = Pick<LineItemProps, 'licensedTerritory' | 'lotteryId'> & {
  addOns: AddOn[]
  prizeCategories: TierTablePrizeCategory[]
  prizeTierCategories: TierTablePrizeTierCategory[]
  type: TierTableType
}

type TierDisplay = {
  fields: LineItemField[]
  jackpot: boolean
  prize?: Maybe<MoneyType>
  type: PrizeTierType
  winners: number
}

type TierTablePrize = Omit<PrizePayload, 'tierInfo'>

type TierTablePrizeCategory = {
  group: string
  name: string
  prizes: TierTablePrize[]
}

type TierTablePrizeTierCategory = {
  group: string
  name: string
  prizeTiers: Pick<PrizeTier, 'jackpot' | 'requirements' | 'tier'>[]
}

const isNumberLottery = (requirements: PrizeTierRequirements): requirements is NumberLottery =>
  !!(requirements as NumberLottery).numberRequirements

const props = defineProps<LotteryTierTableMultipleProps>()

const combinedTierTables = computed(() => {
  const tierTables = props.prizeCategories.map<CombinedTierTable>(({ group, name, prizes }) => ({
    group,
    name,
    prizes: getTierDisplays(name, prizes, group),
  }))

  return rearrangeTierTables(tierTables)
})

const combinedTierTablesByGroup = computed(() =>
  combinedTierTables.value.reduce<Record<string, CombinedTierTable[]>>((acc, tierTable) => {
    if (!tierTable.group) return acc

    const accGroup = acc[tierTable.group] ?? []

    return {
      ...acc,
      [tierTable.group]: [...accGroup, tierTable],
    }
  }, {}),
)

const groups = computed(() =>
  props.prizeCategories.reduce<string[]>((acc, cat) => [...new Set([cat.group, ...acc])], []),
)

const getTierDisplays = (categoryName: string, prizes: TierTablePrize[], groupName: string) => {
  const prizeTierCategory = props.prizeTierCategories.find(
    ({ group, name }) => name === categoryName && group === groupName,
  )
  if (!prizeTierCategory) throw new Error('no matching prize category')

  return prizes.map(({ prize, tier, type, winners }) => {
    const prizeTier = prizeTierCategory.prizeTiers.find((_prizeTier) => _prizeTier.tier === tier)

    if (!prizeTier || !isNumberLottery(prizeTier.requirements)) {
      throw new Error(`no matching prize tier ${tier}, ${typeof tier} ${prizeTier}`)
    }

    const { numberRequirements } = prizeTier.requirements

    return {
      fields: numberRequirements.map(({ amountOfCorrectNumbers, numbersReference }) => ({
        name: numbersReference,
        numbers: new Array<LcLotteryNumber>(amountOfCorrectNumbers).fill({
          value: '',
        }),
      })),
      jackpot: prizeTier.jackpot,
      prize,
      type,
      winners,
    }
  })
}

const rearrangeTierTables = (tierTables: CombinedTierTable[]) => {
  const [defaultTier] = tierTables
  if (defaultTier.prizes.length <= 1) return tierTables

  const [jackpotPrize, ...defaultPrizes] = defaultTier.prizes

  return [
    {
      group: defaultTier.group,
      name: defaultTier.name,
      prizes: [jackpotPrize],
    },
    ...tierTables.slice(1, tierTables.length),
    {
      group: defaultTier.group,
      prizes: defaultPrizes,
    },
  ]
}
</script>

<style scoped lang="scss">
.results-tier-table-combined {
  margin-bottom: 1.5rem;

  :last-of-type {
    margin-bottom: 0;
  }

  td:nth-child(n + 2):last-child {
    font-weight: token('font/weight/medium');
    text-align: right;
  }

  th {
    @include token-paragraph-bold;

    text-align: left;
  }

  .table-head {
    th {
      @include token-headline-h5-headline;
    }

    tr {
      margin-bottom: 0.75rem;
    }
  }
}
</style>
